import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OutboundEmailSettings } from '@xiltrion/correo';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoCargando, EstadoError } from '@xiltrion/ui/bloques/estados';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Icono } from '@xiltrion/ui/icono';
import { apiGet } from '@/api/client';
import { OutboundCard } from '@/pages/entities/EntityMailTab';

interface PlatformMailView {
  source: 'platform' | 'environment' | 'none';
  settings: (OutboundEmailSettings & { hasSecret: boolean }) | null;
  environment: { host: string; port: number; from: string } | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/**
 * Correo de la suite misma (Datalog › Correo): invitaciones, recuperación de contraseña y avisos. El de cada entidad
 * para sus apps está en la ficha de la entidad › Correo.
 */
export function MailAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-mail'], queryFn: () => apiGet<PlatformMailView>('/admin/mail') });
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <EncabezadoPagina titulo="Correo de la suite" descripcion="Con qué servicio y a nombre de quién envía la suite sus invitaciones, enlaces de recuperación y avisos." />
      {q.error ? <EstadoError error={q.error} /> : !q.data ? <EstadoCargando /> : (
        <>
          {q.data.source === 'environment' && q.data.environment && <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Sin configurar aquí, la suite envía con el servidor del entorno ({q.data.environment.host}:{q.data.environment.port}, desde {q.data.environment.from}). En desarrollo es Mailpit.</AlertDescription></Alert>}
          {q.data.source === 'none' && <Alert variant="warning"><Icono nombre="mdi:alert-outline" /><AlertDescription>La suite no tiene correo: no puede enviar invitaciones ni enlaces de recuperación. Configúralo aquí.</AlertDescription></Alert>}
          <OutboundCard target={{ kind: 'platform', saved: q.data.settings, updatedAt: q.data.updatedAt, updatedBy: q.data.updatedBy }} editable onDone={() => qc.invalidateQueries({ queryKey: ['platform-mail'] })} />
          <Alert variant="info"><Icono nombre="mdi:domain" /><AlertDescription>El correo con el que cada entidad le escribe al ciudadano desde sus apps se configura en la ficha de la entidad › Correo.</AlertDescription></Alert>
        </>
      )}
    </div>
  );
}
